import Icon from '../Icon'
import { ColorProps, MarginProps, color, margin } from 'styled-system'
import styled from 'styled-components'

import Text from '../Text'
import React from 'react'
import { HexToRgba, ThemeColor } from 'utils/colors'
import { isLegacy, LeadDetailsDesignVersion } from '../utils/designVersionHelpers'
import Flex from '../Flex'

type Size = 'xxsmall' | 'xsmall' | 'small' | 'smallPlus' | 'medium' | 'large' | 'v2'

type AvatarProps = MarginProps &
  ColorProps & {
    id?: string
    label?: string
    active?: boolean
    disabled?: boolean
    isDeactivatedUser?: boolean
    imgSrc?: string
    inline?: boolean
    placeholder?: string
    size: Size
    onClick?: () => void
    onClickRemove?: ({ id, label }: { id?: string; label?: string }) => void
    designVersion?: LeadDetailsDesignVersion
  }

const fontSizes: { [key: string]: number } = {
  xxsmall: 14,
  xsmall: 14,
  small: 20,
  smallPlus: 12,
  medium: 28,
  large: 32,
}
const fontSizesV2: { [key: string]: number } = {
  ...fontSizes,
  v2: 15,
}

const sizes: { [key: string]: number } = {
  xxsmall: 20,
  xsmall: 24,
  small: 40,
  smallPlus: 32,
  medium: 80,
  large: 100,
}
const sizesV2: { [key: string]: number } = {
  ...sizes,
  v2: 40,
}

const getSize = (size: Size, designVersion: LeadDetailsDesignVersion) =>
  isLegacy(designVersion) ? sizes[size] : sizesV2[size]
const getFontSize = (size: Size, designVersion: LeadDetailsDesignVersion) =>
  isLegacy(designVersion) ? fontSizes[size] : fontSizesV2[size]

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      id,
      label,
      imgSrc,
      inline = false,
      active,
      size,
      placeholder,
      onClick,
      onClickRemove,
      disabled,
      isDeactivatedUser = false,
      backgroundColor,
      color,
      designVersion = 'v1',
      ...marginProps
    },
    ref,
  ) => (
    <Container
      data-testid="avatar-container"
      {...marginProps}
      backgroundColor={backgroundColor}
      color={color as ThemeColor}
      active={active}
      size={size}
      inline={inline}
      designVersion={designVersion}
      onClick={() => !disabled && onClick?.()}
      ref={ref}
    >
      {isDeactivatedUser && (
        <BlurOverlay data-testid="blur-overlay" gap="0" justifyContent="center" alignItems="center">
          <Icon icon={['far', 'minus-octagon']} color="gray5" />
        </BlurOverlay>
      )}
      {imgSrc ? (
        <ImgContainer
          data-testid="avatar-img-container"
          src={imgSrc}
          alt={label ?? 'Avatar'}
          decoding="async"
          fetchpriority="low"
          width={getSize(size, designVersion)}
          height={getSize(size, designVersion)}
        />
      ) : placeholder ? (
        <Text
          data-testid="avatar-text-container"
          color="white"
          fontSize={getFontSize(size, designVersion)}
          fontWeight="bold"
          textTransform="uppercase"
        >
          {placeholder.substring(0, 2)}
        </Text>
      ) : (
        <Icon icon="user-circle" color="white" fontSize={getSize(size, designVersion)} />
      )}
      {!disabled && onClickRemove && (
        <RemoveIcon
          color="red"
          fontSize={getFontSize(size, designVersion)}
          icon={['fas', 'times-circle']}
          onClick={() => {
            onClickRemove({ id, label })
          }}
        />
      )}
    </Container>
  ),
)

const RemoveIcon = styled(Icon)`
  position: absolute;
  right: 0;
  top: 0;
  transform: translate(50%, -50%);
  opacity: 0;
`

const BlurOverlay = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;
  background-color: ${HexToRgba('#EDF1F7', 0.6)};
`

const Container = styled.div<
  MarginProps &
    ColorProps & {
      active?: boolean
      size: string
      imgSrc?: string
      inline: boolean
      designVersion: LeadDetailsDesignVersion
    }
>`
  ${margin}
  background-color: ${(p) => p.theme.colors.gray5};
  ${color}
  flex-shrink: 0;
  position: relative;
  align-items: center;
  border-radius: 50%;
  display: ${(p) => (p.inline ? 'inline-flex' : 'flex')};
  justify-content: center;
  width: ${(p) => (isLegacy(p.designVersion) ? sizes[p.size] : sizesV2[p.size])}px;
  height: ${(p) => (isLegacy(p.designVersion) ? sizes[p.size] : sizesV2[p.size])}px;
  box-sizing: border-box;
  vertical-align: middle;

  &:hover ${RemoveIcon} {
    opacity: 1;
  }
`

const ImgContainer = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  box-sizing: border-box;
`

export default Avatar
